<template>
  <div class="teacher-home navigation-top-app">
    <navigation-top-app :app="app">
      <template v-slot:center>
        <div id="navigation-center">
          {{ app.strings.app.teacher.home.title }}
        </div>
      </template>
    </navigation-top-app>
    <transition name="fade">
      <div id="teacher-home-content" v-if="show">
        <div class="home-main">
        <div class="teacher-data">
          <img src="@/assets/images/avatar.svg" />
          <div>
            {{ app.user.name }}
          </div>
        </div>
        <!-- Desktop: the side menu already has these options, so a greeting
             with the date takes their place. -->
        <div class="home-greeting">
          <div class="home-greeting-hello">{{ greeting }}, {{ firstName }}</div>
          <div class="home-greeting-date">{{ todayLong }}</div>
        </div>
        <menu-vertical
          class="home-menu"
          :app="app"
          :color="'blue'"
          :bold="true"
          :padding="{
            top: 0,
            sides: 0
          }"
          :options="[
            {
              type: 'button',
              icon: ['fas', 'clipboard-list'],
              label: app.strings.app.teacher.home.menu.calendar,
              action: () => {
                openAnimated('calendar')
              }
            },
            {
              type: 'button',
              icon: ['fas', 'fingerprint'],
              label: app.strings.app.teacher.home.menu.biometric,
              action: () => {
                openAnimated('biometric')
              }
            },
            {
              type: 'button',
              icon: ['fas', 'list'],
              label: app.strings.app.teacher.home.menu.students,
              action: () => {
                openAnimated('student.list')
              }
            },
            {
              type: 'button',
              icon: ['fas', 'question-circle'],
              label: app.strings.app.teacher.home.menu.help,
              action: () => {
                openAnimated('help')
              }
            },
            {
              type: 'button',
              style: 'background-color: rgb(207, 69, 92);',
              icon: ['fas', 'power-off'],
              label: app.strings.app.teacher.home.menu.logout,
              action: () => {
                this.app.methods.logout()
              }
            }
          ]"
        />
        </div>

        <!-- Today's classes: above the menu on mobile, main column from tablet up. -->
        <div class="home-today">
          <!-- Classes left started and not ended, of days before (card «clases
               olvidadas», 27-oct): each one is ended from here. -->
          <div class="home-unfinished" v-if="unfinished.length > 0">
            <div class="home-unfinished-title">{{ lateEnd.home_title }} <span class="home-count">{{ unfinished.length }}</span></div>
            <div class="home-unfinished-item" v-for="practice in unfinished" :key="practice.id" v-on:click="openPractice(practice)">
              <div class="home-unfinished-data">
                <b>{{ unfinishedDay(practice) }} · {{ practice.start }}</b>
                <span>{{ practice.student.name }}</span>
              </div>
              <button class="home-unfinished-end" v-on:click.stop="endLate(practice)">{{ lateEnd.button }}</button>
            </div>
          </div>
          <div class="home-today-title">
            {{ app.strings.app.teacher.home.today.title }}
            <span class="home-count" v-if="!today.loading && active(today.data).length > 0">{{ countLabel(active(today.data).length) }}</span>
          </div>
          <div class="home-today-item" v-for="practice in today.data" :key="practice.id" :class="{ended: practice.ended === 1, cancelled: practice.cancelled === 1}" v-on:click="openPractice(practice)">
            <div class="home-today-time">{{ clock(practice.hour, practice.minute) }}</div>
            <div class="home-today-data">
              <div class="home-today-name">{{ practice.student_name }}</div>
              <div class="home-today-meta">{{ practice.vehicle_name }}<template v-if="practice.meeting_point === 'other'"> · <font-awesome-icon :icon="['fas', 'map-marker-alt']" /> {{ practice.meeting_address }}</template></div>
              <!-- A class with an incident, or a recovery, says so (Miguel, 22-oct, 3.7). -->
              <div class="home-mark" v-if="practice.incident">{{ incidentStrings.mark }} · {{ incidentStrings.resolutions_short[practice.incident.resolution] }}</div>
              <div class="home-mark recovery" v-else-if="practice.recovery_of">{{ incidentStrings.mark_recovery }}</div>
            </div>
          </div>
          <div class="home-today-empty" v-if="!today.loading && today.data.length === 0">{{ app.strings.app.teacher.home.today.empty }}</div>
        </div>

        <!-- Next six days, desktop only. -->
        <div class="home-next">
          <div class="home-today-title">{{ app.strings.app.teacher.home.next.title }}</div>
          <div class="home-next-day" v-for="day in nextDays" :key="day.key">
            <div class="home-next-day-name">{{ day.label }} <span class="home-count" v-if="active(day.practices).length > 0">{{ countLabel(active(day.practices).length) }}</span></div>
            <div class="home-next-item" v-for="practice in day.practices.slice(0, 3)" :key="practice.id" :class="{cancelled: practice.cancelled === 1}" v-on:click="openPractice(practice)">
              <b>{{ clock(practice.hour, practice.minute) }}</b>
              <span :title="practice.student_name">{{ practice.student_name }}</span>
            </div>
            <div class="home-next-more" v-if="day.practices.length > 3">{{ app.strings.app.teacher.home.next.more.replace('{n}', day.practices.length - 3) }}</div>
          </div>
          <div class="home-today-empty" v-if="!today.loading && nextDays.length === 0">{{ app.strings.app.teacher.home.next.empty }}</div>
          <a class="home-link" v-on:click="open('calendar')">{{ app.strings.app.teacher.home.next.see_calendar }} <font-awesome-icon :icon="['fas', 'chevron-right']" /></a>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'app-teacher-home-view',
  components: {
  },
  data () {
    return {
      show: false,
      today: {
        loading: false,
        data: [],
        next: []
      },
      unfinished: []
    }
  },
  computed: {
    lateEnd () {
      return this.app.strings.components.practice.late_end
    },
    incidentStrings () {
      return this.app.strings.components.practice.incident
    },
    firstName () {
      return (this.app.user.name || '').trim().split(/\s+/)[0]
    },
    greeting () {
      let hour = new Date().getHours()
      let g = this.app.strings.app.teacher.home.greeting
      return hour < 14 ? g.morning : (hour < 21 ? g.afternoon : g.evening)
    },
    todayLong () {
      let d = new Date()
      let text = d.toLocaleDateString('es-ES', {weekday: 'long', day: 'numeric', month: 'long'})
      return text.charAt(0).toUpperCase() + text.slice(1)
    },
    nextDays () {
      let days = {}
      let order = []
      for (let practice of this.today.next) {
        let key = practice.dayKey
        if (!days[key]) {
          let d = new Date(key + 'T12:00:00')
          let label = d.toLocaleDateString('es-ES', {weekday: 'long', day: 'numeric', month: 'short'})
          days[key] = {key: key, label: label.charAt(0).toUpperCase() + label.slice(1), practices: []}
          order.push(key)
        }
        days[key].practices.push(practice)
      }
      return order.sort().map((key) => days[key])
    }
  },
  props: [
    'app'
  ],
  mounted () {
    this.show = true
    this.todayRead()
    this.unfinishedRead()
  },
  methods: {
    clock (hour, minute) {
      return (hour < 10 ? '0' : '') + hour + ':' + (minute < 10 ? '0' : '') + minute
    },
    // Classes that will happen (not cancelled), for the counters.
    active (practices) {
      return practices.filter((p) => p.cancelled !== 1)
    },
    countLabel (n) {
      let t = this.app.strings.app.teacher.home.today
      return n === 1 ? t.count_one : t.count_other.replace('{n}', n)
    },
    todayRead () {
      let format = (d) => d.getFullYear() + '-' + (d.getMonth() + 1 < 10 ? '0' : '') + (d.getMonth() + 1) + '-' + (d.getDate() < 10 ? '0' : '') + d.getDate()
      let day = new Date()
      let date = format(day)
      let last = new Date()
      last.setDate(last.getDate() + 6)
      this.today.loading = true
      // Today and the next six days. The server returns only this teacher's
      // classes.
      this.$http.post(
        this.app.url.api + this.app.endpoints.practice.filter,
        {start_date: date, end_date: format(last), student: 'all'},
        {
          emulateJSON: true,
          headers: {
            Authorization: 'Token ' + this.app.token
          }
        }
      ).then(response => {
        this.today.loading = false
        if (response.status === 200 && response.body.success === true) {
          let practices = response.body.data.practices.map((p) => {
            let d = new Date(p.date)
            return Object.assign({}, p, {dayKey: d.getUTCFullYear() + '-' + (d.getUTCMonth() + 1 < 10 ? '0' : '') + (d.getUTCMonth() + 1) + '-' + (d.getUTCDate() < 10 ? '0' : '') + d.getUTCDate()})
          }).sort((a, b) => a.dayKey === b.dayKey ? (parseInt(a.hour) * 60 + parseInt(a.minute)) - (parseInt(b.hour) * 60 + parseInt(b.minute)) : a.dayKey.localeCompare(b.dayKey))
          this.today.data = practices.filter((p) => p.dayKey === date)
          this.today.next = practices.filter((p) => p.dayKey !== date)
        }
      }, () => {
        this.today.loading = false
      })
    },
    unfinishedRead () {
      this.$http.get(this.app.url.api + this.app.endpoints.practice.unfinished, {headers: {Authorization: 'Token ' + this.app.token}}).then(response => {
        if (response.status === 200 && response.body.success === true) {
          this.unfinished = response.body.data.practices
        }
      }, () => {})
    },
    unfinishedDay (practice) {
      let d = new Date(practice.date + 'T00:00:00')
      return d.toLocaleDateString('es-ES', {weekday: 'short', day: 'numeric', month: 'short'})
    },
    // To the class page with the form of «Terminar ahora» open.
    endLate (practice) {
      this.$router.push({name: 'app.teacher.practice.menu', params: {id: practice.id}, query: {origin: 'app', navigationLeft: 'false', token: this.app.token, late_end: '1'}})
    },
    openPractice (practice) {
      this.$router.push({name: 'app.teacher.practice.menu', params: {id: practice.id}, query: {origin: 'app', navigationLeft: 'false', token: this.app.token}})
    },
    openAnimated (target) {
      this.show = false
      setTimeout(() => {
        this.open(target)
      }, 500)
    },
    open (target) {
      switch (target) {
        case 'calendar':
          this.$router.push({name: 'app.practice.calendar', query: {origin: 'app', navigationLeft: 'false', token: this.app.token}})
          break;

        case 'student.list':
          this.$router.push({name: 'app.student.list', query: {origin: 'app', navigationLeft: 'false', token: this.app.token}})
          break;

        case 'biometric':
          this.$router.push({name: 'app.teacher.biometric.courses', query: {origin: 'app', navigationLeft: 'false', token: this.app.token}})
          break;

        case 'help':
          this.$router.push({name: 'app.help', query: {origin: 'app', navigationLeft: 'false', token: this.app.token}})
          break;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/variables-app.scss";
@import "@/assets/styles/variables-valkiria.scss";
@import "@/assets/styles/mobile-app.scss";

.teacher-home {
  display: flex;
  flex-direction: column;
  padding: 0 !important;
}

#teacher-home-content {
  display: flex;
  width: 100%;
  min-height: 100%;
  padding: 72px 16px 25px 16px;
  box-sizing: border-box;
  flex-direction: column;
}

.home-unfinished {
  margin: -4px 0 14px;
  padding: 12px;
  border-radius: 14px;
  background-color: #fef2f2;
}

.home-unfinished-title {
  margin-bottom: 6px;
  color: #991b1b;
  font-family: 'Lexend', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.home-unfinished-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-top: 1px solid #fecaca;
  cursor: pointer;
}

.home-unfinished-data {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  color: #1f2a37;
  font-size: 14px;
}

.home-unfinished-data > span {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.home-unfinished-end {
  flex-shrink: 0;
  padding: 8px 12px;
  border: 0;
  border-radius: 10px;
  background-color: #2071b7;
  color: #FFFFFF;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.home-today {
  margin-top: 22px;
  padding: 16px;
  border: 1px solid #e8edf3;
  border-radius: 20px;
  background-color: #FFFFFF;
  box-shadow: 0 12px 30px rgba(20, 67, 107, 0.08);
}

.home-today-title {
  margin-bottom: 8px;
  color: #64748b;
  font-family: 'Lexend', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.home-today-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid #e8edf3;
  cursor: pointer;
}

.home-today-item.cancelled {
  opacity: 0.55;
}

.home-today-time {
  flex: 0 0 auto;
  padding: 6px 10px;
  border-radius: 10px;
  background-color: rgba(32, 113, 183, 0.1);
  color: #2071b7;
  font-family: 'Lexend', sans-serif;
  font-weight: 700;
}

.home-today-item.ended .home-today-time {
  background-color: rgba(22, 163, 74, 0.12);
  color: #16a34a;
}

.home-today-data {
  min-width: 0;
}

.home-today-name {
  overflow: hidden;
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-weight: 600;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.home-mark {
  display: inline-block;
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background-color: #fff4e6;
  color: #b45309;
  font-family: 'Lexend', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.home-mark.recovery {
  background-color: rgba(124, 58, 237, 0.1);
  color: #6d28d9;
}

.home-today-meta {
  color: #64748b;
  font-size: 13px;
  overflow-wrap: anywhere;
}

.home-today-empty {
  padding: 12px 0 4px;
  color: #94a3b8;
  font-size: 14px;
}

@media (min-width: 1024px) {
  #teacher-home-content {
    display: grid;
    grid-template-columns: 1fr minmax(320px, 400px);
    gap: 28px;
    align-items: start;
    padding-top: 32px;
  }

  // The greeting already says where you are, and this screen has no way back.
  .teacher-home #navigation-top-app {
    display: none;
  }

  .home-today {
    position: sticky;
    top: 92px;
    margin-top: 12px;
  }
}

.home-greeting,
.home-next {
  display: none;
}

.home-count {
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background-color: rgba(32, 113, 183, 0.1);
  color: #2071b7;
  font-size: 11px;
  letter-spacing: 0;
  text-transform: none;
}

.home-next-day {
  padding: 10px 0;
  border-top: 1px solid #e8edf3;
}

.home-next-day-name {
  margin-bottom: 6px;
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-size: 14px;
  font-weight: 600;
}

.home-next-item {
  display: flex;
  gap: 10px;
  padding: 3px 0;
  color: #475569;
  font-size: 13px;
  cursor: pointer;
}

.home-next-item > b {
  flex: 0 0 auto;
  color: #2071b7;
  font-family: 'Lexend', sans-serif;
}

.home-next-item > span {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.home-next-item.cancelled {
  opacity: 0.55;
  text-decoration: line-through;
}

.home-next-more {
  color: #94a3b8;
  font-size: 12px;
}

.home-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  color: #2071b7 !important;
  font-family: 'Lexend', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.home-link > svg {
  font-size: 11px;
}

// Phone and tablet: the classes come before the menu, which is what a teacher
// opens the app to see. The wrapper steps aside so its children can be ordered.
@media (max-width: 1023px) {
  .home-main {
    display: contents;
  }

  .teacher-data {
    order: 1;
  }

  .home-today {
    order: 2;
    margin-top: 0;
  }

  .home-menu {
    order: 3;
    margin-top: 4px;
  }
}

// Tablet: there is room for two columns, and no side menu yet.
@media (min-width: 768px) and (max-width: 1023px) {
  #teacher-home-content {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 300px;
    grid-template-rows: auto auto 1fr;
    grid-template-areas:
      "data data"
      "today menu"
      "today next";
    column-gap: 20px;
    align-items: start;
    padding: 72px 24px 25px;
  }

  .teacher-data {
    grid-area: data;
  }

  .home-today {
    grid-area: today;
  }

  // Its rows bring a 10px margin of their own: line it up with the classes.
  .home-menu {
    grid-area: menu;
    margin-top: -10px;
  }

  .home-next {
    grid-area: next;
    margin-top: 14px;
  }
}

@media (min-width: 768px) {
  .home-next {
    display: block;
    padding: 16px 18px;
    border: 1px solid #e8edf3;
    border-radius: 20px;
    background-color: #FFFFFF;
    box-shadow: 0 12px 30px rgba(20, 67, 107, 0.08);
  }
}

@media (min-width: 1024px) {
  #teacher-home-content {
    grid-template-columns: 1fr minmax(300px, 360px) !important;
    grid-template-areas:
      "greeting greeting"
      "today next";
    align-content: start;
  }

  .home-main {
    grid-area: greeting;
  }

  .home-main .teacher-data,
  .home-main .home-menu {
    display: none !important;
  }

  .home-greeting {
    display: block;
    padding: 0 4px;
  }

  .home-greeting-hello {
    color: #0f172a;
    font-family: 'Lexend', sans-serif;
    font-size: 28px;
    font-weight: 700;
  }

  .home-greeting-date {
    margin-top: 2px;
    color: #64748b;
    font-size: 15px;
  }

  .home-today {
    grid-area: today;
    position: static !important;
    margin-top: 0 !important;
    padding: 20px 22px;
  }

  .home-today-item {
    padding: 14px 0;
  }

  .home-today-name {
    font-size: 16px;
  }

  .home-next {
    grid-area: next;
  }
}

.teacher-data {
  width: 100%;
  padding: 12px;
  box-sizing: border-box;
  text-align: center;
}

.teacher-data > img {
  width: 100%;
  max-width: 128px;
  padding: 6px 0;
  box-sizing: border-box;
}

.teacher-data > div {
  font-size: 22px;
  color: $text-color-1;
  padding: 24px 12px;
  box-sizing: border-box;
}
</style>
